/**
 * The Gantt's row model, kept pure so the geometry can be unit-tested without a DOM
 * (`specs/M4-V3-gantt.md` §5, §9 group B).
 *
 * Everything the renderer paints is derived here: which column a bar comes from, the bar's
 * inclusive span, whether it is a milestone, the group rollup's span, and the dependency edges.
 * The components below it only place boxes.
 */
import {
  addDays,
  barColumnFor,
  cellSpan,
  dayIndex,
  isBoardGroupToken,
  isUngroupedToken,
  spanOfDays,
  type GanttViewSettings,
} from '@elizmonday/shared';
import { colors } from '@elizmonday/ui';
import type { BoardColumn, BoardGroupView, ItemRow, Person } from '../../board-view/data/types';

/** F7 U1: no raw hex outside `packages/ui/src/tokens`. */
const EMPTY_STATUS_COLOR = colors.emptyStatus;

export interface GanttBarModel {
  readonly itemId: string;
  readonly name: string;
  readonly groupId: string;
  /** The date/timeline column this bar came from; the cell a drag writes. */
  readonly columnId: string;
  readonly from: string;
  readonly to: string;
  /** A zero-length span — monday draws the 23 px diamond, not a bar (`[V3§2.1]`). */
  readonly milestone: boolean;
  readonly color: string;
  /** The text drawn to the right of the bar, from `label_by_id`. */
  readonly label: string;
  /** Predecessor item ids from the board's `dependency` column. */
  readonly dependsOn: readonly string[];
}

export interface GanttGroupModel {
  readonly id: string;
  readonly title: string;
  readonly color: string;
  readonly collapsed: boolean;
  readonly bars: readonly GanttBarModel[];
  /** The rollup's span: earliest `from` to latest `to` of the group's bars, or `null`. */
  readonly rollup: { readonly from: string; readonly to: string } | null;
}

/**
 * monday's one unnamed bucket, and the key its collapsed state is stored under on the view row
 * (`collapsedGroupKeys`). The group head writes it and `mergeIntoOneGroup` reads it back.
 */
export const UNGROUPED_GANTT_GROUP_ID = '__ungrouped__';

/** The date-ish columns a bar may come from. */
export function dateColumnsOf(columns: readonly BoardColumn[]): BoardColumn[] {
  return columns.filter((column) => column.type === 'timeline' || column.type === 'date');
}

/** The board's one dependency column, if it has one. */
export function dependencyColumnOf(columns: readonly BoardColumn[]): BoardColumn | undefined {
  return columns.find((column) => column.type === 'dependency');
}

/**
 * The ids a `dependency` cell links to. monday's canonical value is `{item_ids:[…]}`, and the
 * ids arrive as numbers from the API and as strings from our own writes (`[T2§5.19]`).
 */
export function dependsOnOf(value: unknown): string[] {
  if (value === null || value === undefined || typeof value !== 'object') return [];
  const ids = (value as { item_ids?: unknown }).item_ids;
  if (!Array.isArray(ids)) return [];
  return ids.map((id) => String(id)).filter((id) => id !== '' && id !== 'null');
}

/**
 * A bar's colour. `color_by_id` names a column whose bucket colour wins; `group`/`groups` and an
 * unset colour-by both fall back to the group's own colour, which is what every one of the four
 * production Gantts that store `{group:true}` renders.
 */
export function barColorOf(input: {
  readonly settings: Pick<GanttViewSettings, 'colorBy'>;
  readonly groupColor: string;
  readonly values: Readonly<Record<string, unknown>>;
  readonly columns: readonly BoardColumn[];
  readonly people: readonly Person[];
}): string {
  const token = input.settings.colorBy;
  if (token === null || isBoardGroupToken(token)) return input.groupColor;
  const column = input.columns.find((entry) => entry.id === token);
  if (column === undefined) return input.groupColor;
  const value = input.values[token];
  if (column.type === 'status' && value !== null && typeof value === 'object') {
    const labels = (column.settings as { labels_colors?: Record<string, { color?: string }> } | null)?.labels_colors;
    const index = (value as { index?: number }).index;
    const hex = index === undefined ? undefined : labels?.[String(index)]?.color;
    if (typeof hex === 'string') return hex;
  }
  if (column.type === 'people' && value !== null && typeof value === 'object') {
    const first = (value as { personsAndTeams?: { id: number }[] }).personsAndTeams?.[0];
    const person = input.people.find((entry) => String(entry.id) === String(first?.id));
    if (person !== undefined) return person.color;
  }
  return input.groupColor;
}

/** The text drawn beside the bar. `label_by_id: {name:true}` is what every real row stores. */
export function barLabelOf(
  settings: Pick<GanttViewSettings, 'labelBy'>,
  item: ItemRow,
  columns: readonly BoardColumn[],
): string {
  const token = settings.labelBy;
  if (token === null || token === 'name') return item.name;
  const column = columns.find((entry) => entry.id === token);
  if (column === undefined) return item.name;
  const value = item.values[token];
  if (typeof value === 'string') return value;
  if (value !== null && typeof value === 'object') {
    const text = (value as { text?: unknown }).text;
    if (typeof text === 'string') return text;
  }
  return item.name;
}

export function buildGanttGroups(input: {
  readonly settings: GanttViewSettings;
  readonly groups: readonly BoardGroupView[];
  readonly rowsOf: (groupId: string) => readonly ItemRow[];
  readonly columns: readonly BoardColumn[];
  readonly people: readonly Person[];
}): GanttGroupModel[] {
  const dateColumns = dateColumnsOf(input.columns);
  const dependency = dependencyColumnOf(input.columns);
  const out: GanttGroupModel[] = [];

  for (const group of input.groups) {
    const bars: GanttBarModel[] = [];
    for (const item of input.rowsOf(group.id)) {
      const columnId = barColumnFor({ settings: input.settings, columns: dateColumns, values: item.values });
      if (columnId === null) continue;
      const span = cellSpan(item.values[columnId]);
      if (span === null) continue;
      bars.push({
        itemId: item.id,
        name: item.name,
        groupId: group.id,
        columnId,
        from: span.start,
        to: span.end,
        milestone: span.start === span.end,
        color: barColorOf({
          settings: input.settings,
          groupColor: group.color,
          values: item.values,
          columns: input.columns,
          people: input.people,
        }),
        label: barLabelOf(input.settings, item, input.columns),
        dependsOn: dependency === undefined ? [] : dependsOnOf(item.values[dependency.id]),
      });
    }
    const rollup = spanOfDays(bars.flatMap((bar) => [bar.from, bar.to]));
    out.push({
      id: group.id,
      title: group.title,
      color: group.color,
      collapsed: input.settings.collapsedGroupKeys.includes(group.id),
      bars,
      rollup,
    });
  }
  return out;
}

/**
 * `group_by_id: {none:true}` — monday renders **one unnamed bucket**, not the board's groups
 * (measured on the sandbox Gantt, whose single `tl-group` carries the class `ungrouped` and an
 * empty title). Four of the eight production Gantts are stored that way.
 */
export function mergeIntoOneGroup(
  groups: readonly GanttGroupModel[],
  /**
   * **P-149.** The merged bucket's own collapsed state, read back from the view row exactly as
   * the grouped path reads a real group id. Without it the merge hard-coded `collapsed: false`
   * and threw away what `buildGanttGroups` had just resolved, so clicking the group head on an
   * **ungrouped** gantt — four of the account's eight production gantts, and every gantt this
   * product creates — wrote `collapsedGroupKeys: ["__ungrouped__"]` and a `board_view_updated`
   * row that nothing ever read back: every bar stayed on the chart, before and after a reload.
   */
  settings?: Pick<GanttViewSettings, 'collapsedGroupKeys'>,
): GanttGroupModel[] {
  const bars = groups.flatMap((group) => group.bars);
  return [{
    id: UNGROUPED_GANTT_GROUP_ID,
    title: '',
    color: groups[0]?.color ?? EMPTY_STATUS_COLOR,
    collapsed: (settings?.collapsedGroupKeys ?? []).includes(UNGROUPED_GANTT_GROUP_ID),
    bars,
    rollup: spanOfDays(bars.flatMap((bar) => [bar.from, bar.to])),
  }];
}

/**
 * The range the axis opens on: the data's own extent, padded by 10 % of its span on each side
 * so the first and last bar are not flush against the edge, with a one-month floor so a board
 * whose items all share a day still gets a readable header (`[V3§5]`).
 */
export function ganttRange(groups: readonly GanttGroupModel[], today: string): { from: string; to: string } {
  const days = groups.flatMap((group) => group.bars.flatMap((bar) => [bar.from, bar.to]));
  const span = spanOfDays([...days, today]);
  if (span === null) return { from: today, to: today };
  // 10 % of the span on each side, never less than a week: without it the earliest bar sits at
  // x = 0, where its left resize handle falls **under the sticky side panel** and cannot be
  // grabbed — found by `verify-views-gantt-local.mjs`, not by reading the code.
  const pad = Math.max(7, Math.round((dayIndex(span.to) - dayIndex(span.from) + 1) * 0.1));
  return { from: addDays(span.from, -pad), to: addDays(span.to, pad) };
}

/** The rows a virtualiser lays out: group headers and bar rows, in paint order. */
export type GanttRow =
  | { readonly kind: 'group'; readonly group: GanttGroupModel; readonly height: number }
  | { readonly kind: 'bar'; readonly group: GanttGroupModel; readonly bar: GanttBarModel; readonly height: number };

export function flattenGanttRows(
  groups: readonly GanttGroupModel[],
  metrics: { groupHeaderHeight: number; rowHeight: number },
): GanttRow[] {
  const out: GanttRow[] = [];
  for (const group of groups) {
    out.push({ kind: 'group', group, height: metrics.groupHeaderHeight });
    if (group.collapsed) continue;
    for (const bar of group.bars) out.push({ kind: 'bar', group, bar, height: metrics.rowHeight });
  }
  return out;
}

export interface DependencyEdge {
  readonly fromItemId: string;
  readonly toItemId: string;
}

/** Only the edges whose **both** ends are drawn; a link to an off-page item has no arrow. */
export function dependencyEdges(rows: readonly GanttRow[]): DependencyEdge[] {
  const drawn = new Set(rows.filter((row) => row.kind === 'bar').map((row) => (row as { bar: GanttBarModel }).bar.itemId));
  const out: DependencyEdge[] = [];
  for (const row of rows) {
    if (row.kind !== 'bar') continue;
    for (const predecessor of row.bar.dependsOn) {
      if (drawn.has(predecessor)) out.push({ fromItemId: predecessor, toItemId: row.bar.itemId });
    }
  }
  return out;
}

/**
 * The cell value a drag writes (`[V3§6]`). A `timeline` moves both ends or resizes one; a
 * `date` keeps its time and moves the single day. `null` when the gesture would invert the
 * span, which is the clamp acceptance C3 asks for.
 */
export function dragValue(input: {
  readonly current: unknown;
  readonly role: 'bar' | 'handle-left' | 'handle-right';
  readonly deltaDays: number;
  readonly shift: (day: string, delta: number) => string;
}): Record<string, unknown> | null {
  const value = input.current;
  if (value === null || value === undefined || typeof value !== 'object') return null;
  const record = value as Record<string, unknown>;

  if (typeof record['date'] === 'string') {
    // A date has one end; every role moves it, and a resize of a milestone is a move.
    return { ...record, date: input.shift(record['date'], input.deltaDays) };
  }

  const from = record['from'];
  const to = record['to'];
  if (typeof from !== 'string' || typeof to !== 'string') return null;

  if (input.role === 'bar') {
    return { ...record, from: input.shift(from, input.deltaDays), to: input.shift(to, input.deltaDays) };
  }
  if (input.role === 'handle-left') {
    const next = input.shift(from, input.deltaDays);
    return next > to ? null : { ...record, from: next, to };
  }
  const next = input.shift(to, input.deltaDays);
  return next < from ? null : { ...record, from, to: next };
}

/** The group-by state a Gantt sends with its read (`[V3§5]` rule 1). */
export function ganttQueryGroupBy(settings: Pick<GanttViewSettings, 'groupBy'>):
  | { columnId: string; direction: 'asc'; showEmptyGroups: boolean }
  | undefined {
  const token = settings.groupBy;
  if (token === null || isUngroupedToken(token) || isBoardGroupToken(token)) return undefined;
  return { columnId: token, direction: 'asc', showEmptyGroups: false };
}
